// Harvey Mudd College: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Harvey Mudd's Common Data Set 2025-2026 (Institutional Research): C1
//    applicants, admits and enrollees for Fall 2025 with the residency table,
//    C8 test policy for Fall 2027 and the score date, C21/C22 plans. Quirks:
//    C21 labels its early decision counts (594 applied, 109 admitted) "For
//    the Fall 2024 entering class", so they are left out; the C21 and C22
//    check boxes are hard to read in the PDF, so the plans come from the
//    admissions pages (two binding Early Decision rounds, no early action),
//    which match the C21 dates. G1 gives tuition of $72,669 (first-year) and
//    $75,669 for 2026-2027, which does not match Financial Aid's 2026-2027
//    tuition of $76,879, so costs come from Financial Aid's 2026-2027 Cost of
//    Attendance instead.
//  - hmc.edu Admission: Apply to Harvey Mudd (deadlines, decision release and
//    reply dates, testing deadlines), the Counselor Page (test-optional for
//    students entering in Fall 2027) and First-Year Applicants (the Early
//    Decision commitment).
//  - College Scorecard API, school id 115409 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Harvey Mudd Common Data Set 2025-2026',
        url: 'https://www.hmc.edu/institutional-research/wp-content/uploads/sites/42/2026/03/CDS-HMC-2025.2026_shared.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'Harvey Mudd Admission, Apply to Harvey Mudd',
        url: 'https://www.hmc.edu/admission/apply/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Harvey Mudd Admission, First-Year Applicants',
        url: 'https://www.hmc.edu/admission/apply/first-year-students/',
        read: '2026-10-01',
    },
    counselor: {
        label: 'Harvey Mudd Admission, Counselor Page',
        url: 'https://www.hmc.edu/admission/counselor-page/',
        read: '2026-10-01',
    },
    coa: {
        label: 'Harvey Mudd Financial Aid, 2026-2027 Cost of Attendance',
        url: 'https://www.hmc.edu/admission/afford/cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?115409-Harvey-Mudd-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'harvey-mudd-college',
    kbId: 'harvey_mudd_college',
    name: 'Harvey Mudd College',
    shortName: 'Harvey Mudd',
    aliases: ['Mudd'],
    location: 'Claremont, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Harvey Mudd brand color palette); decoration only.
    branding: {
        primary: '#000000',
        secondary: '#FDB913',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/harvey_mudd_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 5217,
        admitted: 643,
        enrolled: 236,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2291, admitted: 283 },
            { group: 'Out-of-state', applied: 1631, admitted: 291 },
            { group: 'International', applied: 1295, admitted: 69 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Harvey Mudd\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'The Common Data Set gives early decision counts only for an earlier class, so this page shows no early decision acceptance rate. Harvey Mudd has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 15', decision: 'December 15', binding: true },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'April 1', binding: false },
        ],
        note: 'Reply dates are January 8 for Early Decision I, February 26 for Early Decision II and May 1 for Regular Decision. The optional interview deadline is November 15 for Early Decision I and December 15 for Early Decision II and Regular Decision, and the merit scholarship deadline is February 1. The Common App cutoff for documents is January 21 for Early Decision II and February 6 for Regular Decision. A deadline that falls on a weekend or holiday moves to the next business day.',
        source: 'apply',
        binding: 'Early Decision is binding: if you are admitted in either round, you withdraw your other applications. The one exception is when your family decides the financial aid offer is not enough. If you are denied in either Early Decision round, you cannot apply Regular Decision the same year.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'For students entering in fall 2027, the SAT and ACT are optional, and students who do not send scores are not at a disadvantage. If you send scores, you can self-report them on your application or on the HMC Applicant Hub; only admitted students who enroll send official score reports.',
        source: 'counselor',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants, with scores due by February 5.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'Harvey Mudd calls its test-optional policy a pilot and has extended it through 2027 entry. It has not said whether students entering in fall 2028 must send SAT or ACT scores.', source: 'apply' },
        ],
        source: 'counselor',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 76879 },
            { item: 'Student body fee', amount: 401 },
            { item: 'New student orientation fee (fall only)', amount: 250 },
            { item: 'Housing (on campus)', amount: 13293 },
            { item: 'Food (19-meal plan)', amount: 11489 },
            { item: 'Books and supplies', amount: 800 },
            { item: 'Personal expenses', amount: 1400 },
            { item: 'Transportation', amount: null },
        ],
        total: 104512,
        note: 'For a first-year student living on campus. Transportation varies by distance to the college, so it is not in the $104,512 total.',
        source: 'coa',
        netPrice: {
            average: 39347,
            byIncome: [
                { income: '$0 to $30,000', amount: 41333 },
                { income: '$30,001 to $48,000', amount: 11487 },
                { income: '$48,001 to $75,000', amount: 21515 },
                { income: '$75,001 to $110,000', amount: 16864 },
                { income: 'Over $110,000', amount: 58366 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Harvey Mudd\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
